import React, { useContext, useEffect, useMemo, useState } from "react";
import { Mail } from "lucide-react";
import { FieldConfigurationContext } from "../../../../lib/context/FieldConfigurationContext";
import { SessionContext } from "../../../../lib/context/SessionContext";
import SessionAPI from "../../../../lib/api/Session";
import Button from "../../../common/Button";
import "./LicensesDashboard.css";

export default function LicensesDashboard({ onViewDetails }) {
  const { licenses, fetchLicenses } = useContext(FieldConfigurationContext);
  const sessionContext = useContext(SessionContext);
  const [companyName, setCompanyName] = useState("Your Company");

  // Fetch licenses on mount
  useEffect(() => {
    fetchLicenses();
  }, [fetchLicenses]);

  // Fetch company name from multiple sources
  useEffect(() => {
    const getCompanyName = async () => {
      // Try to get company name from SessionContext first
      // Check multiple possible locations in the session data
      let name = null;

      // Check sessionContext.data.company.name
      if (sessionContext?.data?.company?.name) {
        name = sessionContext.data.company.name;
      }
      // Check sessionContext.data.name (from /authorization response)
      else if (sessionContext?.data?.name) {
        name = sessionContext.data.name;
      }
      // Check sessionContext.company.name
      else if (sessionContext?.company?.name) {
        name = sessionContext.company.name;
      }

      // Check sessionStorage for company name resolved during permission check (user scope)
      if (!name) {
        name = sessionStorage.getItem("field_company_name") || null;
      }

      // If we found a name, use it
      if (name) {
        setCompanyName(name);
        return;
      }

      // If no company name in session, try to fetch from API
      try {
        const token = sessionStorage.getItem("token");
        if (token) {
          const response = await SessionAPI.validateToken(token);
          if (response.status === 200 && response.data?.name) {
            setCompanyName(response.data.name);
          }
        }
      } catch (error) {
        console.error("Failed to fetch company name:", error);
        // Keep default "Your Company"
      }
    };

    getCompanyName();
  }, [sessionContext]);

  // Transform API license data to UI format
  const licenseData = useMemo(() => {
    if (!licenses.data || Object.keys(licenses.data).length === 0) {
      return {
        time: { used: 0, total: 0, current: 0, other: 0, available: 0 },
        expense: { used: 0, total: 0, current: 0, other: 0, available: 0 },
        project: { used: 0, total: 0, current: 0, other: 0, available: 0 },
      };
    }

    // Map API keys to UI keys and field names
    return {
      time: {
        used: licenses.data.time?.used || 0,
        total: licenses.data.time?.limit || 0,
        current: licenses.data.time?.current || 0,
        other: licenses.data.time?.other || 0,
        available: licenses.data.time?.available || 0,
      },
      expense: {
        used: licenses.data.expenses?.used || 0,
        total: licenses.data.expenses?.limit || 0,
        current: licenses.data.expenses?.current || 0,
        other: licenses.data.expenses?.other || 0,
        available: licenses.data.expenses?.available || 0,
      },
      project: {
        used: licenses.data.projectManagement?.used || 0,
        total: licenses.data.projectManagement?.limit || 0,
        current: licenses.data.projectManagement?.current || 0,
        other: licenses.data.projectManagement?.other || 0,
        available: licenses.data.projectManagement?.available || 0,
      },
    };
  }, [licenses.data]);

  // Get license type details
  const getLicenseTypeLabel = (type) => {
    const labels = {
      time: "Time Users",
      expense: "Expense Users",
      project: "Project Management Users",
    };
    return labels[type] || type;
  };

  // Calculate utilization percentage
  const getUtilization = (used, total) => {
    return Math.round((used / total) * 100);
  };

  // Get color based on utilization
  const getUtilizationColor = (utilization) => {
    if (utilization >= 96) return "red"; // 96-100%+ = Red
    if (utilization >= 71) return "yellow"; // 71-95% = Yellow
    return "green"; // 0-70% = Green
  };

  return (
    <div className="licenses-dashboard">
      <div className="licenses-dashboard__header">
        <div>
          <h2 className="licenses-dashboard__title">
            {companyName} License Management
          </h2>
          <p className="licenses-dashboard__description">
            Monitor and manage your field user license allocations across Time
            Entry, Expense, and Project Management modules.
          </p>
        </div>
      </div>

      {licenses.loading && (
        <div className="licenses-dashboard__loading">
          <div className="spinner"></div>
          <p>Loading license data...</p>
        </div>
      )}

      {!licenses.loading && licenses.error === "unavailable" && (
        <div className="licenses-dashboard__unavailable">
          <p>License data is unavailable.</p>
        </div>
      )}

      {!licenses.loading &&
        licenses.error &&
        licenses.error !== "unavailable" && (
          <div className="licenses-dashboard__error">
            <p>Error loading licenses: {licenses.error}</p>
            <Button
              className="button--primary"
              onClick={() => fetchLicenses(true)}
            >
              Retry
            </Button>
          </div>
        )}

      {!licenses.loading && !licenses.error && (
        <div className="licenses-dashboard__grid">
          {Object.keys(licenseData).map((type) => {
            const data = licenseData[type];
            const utilization = getUtilization(data.used, data.total);
            const color = getUtilizationColor(utilization);

            return (
              <div key={type} className="license-card">
                <div className="license-card__header">
                  <h3 className="license-card__title">
                    {getLicenseTypeLabel(type)}
                  </h3>
                </div>
                <div className="license-card__body">
                  <div className="license-card__seats">
                    <span className="license-card__used">{data.used}</span>
                    <span className="license-card__total">
                      {" "}
                      of {data.total}
                    </span>
                  </div>
                  <div className="license-card__progress">
                    <div
                      className={`license-card__progress-bar license-card__progress-bar--${color}`}
                      style={{ width: `${utilization}%` }}
                    />
                  </div>
                  <div className="license-card__utilization">
                    {utilization}% utilized
                    {data.other > 0 && (
                      <span className="license-card__breakdown">
                        {" "}
                        ({data.current} by your company, {data.other} by others)
                      </span>
                    )}
                  </div>
                </div>
                <div className="license-card__footer">
                  <Button
                    className="button--secondary"
                    onClick={() => onViewDetails(type)}
                  >
                    View Details
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="licenses-dashboard__contact">
        <div className="licenses-dashboard__contact-icon">
          <Mail size={24} />
        </div>
        <div className="licenses-dashboard__contact-content">
          <h3 className="licenses-dashboard__contact-title">
            Need More Licenses?
          </h3>
          <p className="licenses-dashboard__contact-description">
            Contact your Deltek representative to purchase additional user
            licenses for any capability.
          </p>
          <Button
            className="button--primary"
            onClick={() => {
              const subject = encodeURIComponent(
                `ComputerEase Field License Update for ${companyName}`
              );

              window.location.href = `mailto:ComputerEaseSalesTeam@deltek.com?subject=${subject}`;
            }}
          >
            Contact Sales
          </Button>
        </div>
      </div>
    </div>
  );
}
